import EventEmitter from 'events';
import createTimeBasedCongestionLevelDefault from './congestionLevelCalculators/timeBasedCongestionLevel';
import createBasicCongestionLevelDefault from './congestionLevelCalculators/basicCongestionLevel';
import createCongestionLevelStatsDefault from './congestionLevelStats';
import createMovingAverageTrackerDefault from './exponentialMovingAverageTracker';
import getFairQualityBandwidthForResolutionDefault from './getFairQualityBandwidthForResolution';
import { LOW } from './congestionLevels';
import { NETWORK_CONDITION, NETWORK_CONDITION_REASON } from '../../../helpers/networkConditionConstants';
import createCongestionDataCsvHelper from './congestionDataCsvHelper';
import calculateNetworkConditionDefault from './calculateNetworkCondition';

const createCongestionLevelEstimator = (deps = {}) => {
  const ee = new EventEmitter();
  const {
    getStats,
    createMovingAverageTracker = createMovingAverageTrackerDefault,
    createTimeBasedCongestionLevel = createTimeBasedCongestionLevelDefault,
    createBasicCongestionLevel = createBasicCongestionLevelDefault,
    createCongestionLevelStats = createCongestionLevelStatsDefault,
    getFairQualityBandwidthForResolution = getFairQualityBandwidthForResolutionDefault,
    calculateNetworkCondition = calculateNetworkConditionDefault,
  } = deps;

  const congestionLevelTimeBased = createTimeBasedCongestionLevel();
  const congestionLevelBasic = createBasicCongestionLevel();
  const audioPacketLossStats = createMovingAverageTracker();
  const videoPacketLossStats = createMovingAverageTracker();
  const videoFramesPerSecondStats = createMovingAverageTracker();
  const bandwidthStats = createMovingAverageTracker();
  let resolution;
  const congestionDataCsvHelper = createCongestionDataCsvHelper();
  let prevNetworkCondition = NETWORK_CONDITION.GOOD;

  const getTimeBasedCongestionLevel = () => {
    if (audioPacketLossStats.getDataPointCount() === 0) {
      return { level: LOW, reason: NETWORK_CONDITION_REASON.NONE };
    }
    return congestionLevelTimeBased.getLevel({
      audioPacketLoss: audioPacketLossStats.getMovingAverageValue(),
    });
  };

  const getBasicCongestionLevel = () => {
    if (bandwidthStats.getDataPointCount() === 0
    || audioPacketLossStats.getDataPointCount() === 0) {
      return { level: LOW, reason: NETWORK_CONDITION_REASON.NONE };
    }
    return congestionLevelBasic.getLevel({
      bandwidth: bandwidthStats.getMovingAverageValue(),
      audioPacketLoss: audioPacketLossStats.getMovingAverageValue(),
      bandwidthFairThreshold: getFairQualityBandwidthForResolution(resolution),
    });
  };

  const addLoggingDataPoint = ({
    audioPacketLoss,
    videoPacketLoss,
    bandwidth,
    videoWidth,
    videoHeight,
  }) => {
    congestionDataCsvHelper.addData({
      timestamp: Date.now(),
      rawAudioPL: audioPacketLoss?.toFixed(3),
      rawVideoPL: videoPacketLoss?.toFixed(3),
      rawBW: parseInt(bandwidth, 10),
      videoWidth,
      videoHeight,
      avgAudioPL: audioPacketLossStats.getMovingAverageValue().toFixed(3),
      avgVideoPL: videoPacketLossStats.getMovingAverageValue().toFixed(3),
      avgBW: parseInt(bandwidthStats.getMovingAverageValue(), 10),
    });
  };

  const getAlgorithmName = ({ timeLevel, basicLevel }) => {
    if (basicLevel === timeLevel) {
      return 'basic,timeBased';
    } else if (basicLevel > timeLevel) {
      return 'basic';
    }
    return 'timeBased';
  };

  const getLoggingMetadata = ({ congestionLevel, timeLevel, basicLevel, stats }) => {
    if (congestionLevel === LOW) {
      return { stats };
    }
    // eslint-disable-next-line consistent-return
    return {
      resolution,
      algorithm: getAlgorithmName({ timeLevel, basicLevel }),
      videoPacketLoss: videoPacketLossStats.getMovingAverageValue(),
      audioPacketLoss: audioPacketLossStats.getMovingAverageValue(),
      bandwidth: bandwidthStats.getMovingAverageValue(),
      payload: congestionDataCsvHelper.getCsvString(),
      stats,
    };
  };

  const getCongestionLevel = (
    stats,
    videoWidth,
    videoHeight,
    videoSourceWidth,
    videoSourceHeight,
    videoSourceFramesPerSecond
  ) => {
    const timeCongestion = getTimeBasedCongestionLevel();
    const basicCongestion = getBasicCongestionLevel();
    const { level: congestionLevel, reason: congestionReason } =
      basicCongestion.level >= timeCongestion.level ? basicCongestion : timeCongestion;
    const videoFramesPerSecond = videoFramesPerSecondStats.getDataPointCount() === 0 ?
      null :
      videoFramesPerSecondStats.getMovingAverageValue();
    const { networkCondition, networkConditionReason } = calculateNetworkCondition({
      congestionLevel,
      congestionReason,
      videoWidth,
      videoHeight,
      videoFramesPerSecond,
      videoSourceWidth,
      videoSourceHeight,
      videoSourceFramesPerSecond,
      prevNetworkCondition,
    });
    prevNetworkCondition = networkCondition;

    ee.emit('congestionLevel', congestionLevel, getLoggingMetadata({ congestionLevel, timeLevel: timeCongestion.level, basicLevel: basicCongestion.level, stats }));
    ee.emit('networkCondition', {
      networkCondition,
      networkConditionReason,
      stats,
    });
  };

  const onStatsAvailable = ({
    audioPacketLoss,
    videoPacketLoss,
    bandwidth,
    videoWidth,
    videoHeight,
    videoFramesPerSecond,
    videoSourceWidth,
    videoSourceHeight,
    videoSourceFramesPerSecond,
    stats,
  }) => {
    if (resolution && (resolution.width !== videoWidth || resolution.height !== videoHeight)) {
      audioPacketLossStats.reset();
      videoPacketLossStats.reset();
      videoFramesPerSecondStats.reset();
      bandwidthStats.reset();
    }
    resolution = { width: videoWidth, height: videoHeight };
    bandwidthStats.addValue(bandwidth);
    videoPacketLossStats.addValue(videoPacketLoss);
    audioPacketLossStats.addValue(audioPacketLoss);
    videoFramesPerSecondStats.addValue(videoFramesPerSecond);
    addLoggingDataPoint({ audioPacketLoss, videoPacketLoss, bandwidth, videoWidth, videoHeight });
    getCongestionLevel(
      stats,
      videoWidth,
      videoHeight,
      videoSourceWidth,
      videoSourceHeight,
      videoSourceFramesPerSecond
    );
  };

  const congestionLevelStats = createCongestionLevelStats(getStats);
  congestionLevelStats.on('statsAvailable', (stats) => {
    onStatsAvailable(stats);
  });

  return Object.assign(ee, {
    start() {
      congestionLevelStats.start();
    },
    stop() {
      congestionLevelStats.stop();
      congestionDataCsvHelper.reset();
    },
  });
};

export default createCongestionLevelEstimator;
